import { formatLocation } from '@ms-cloudpack/path-utilities';
import {
  red,
  bulletedList,
  plural,
  indent,
  type TaskReporterTaskResult,
  type TaskReporter,
} from '@ms-cloudpack/task-reporter';
import type { BundleRequest } from '../types/BundleRequest.js';
import { normalizeRelativePath } from '@ms-cloudpack/path-string-parsing';
import { getFileSizeSync } from './getFileSizeSync.js';
import path from 'path';

/**
 * Format a bundle result into a TaskReporter task result.
 *
 * NOTE: The info as formatted here will only be shown when a task is completed, not in the summary.
 * Reporter configuration may change over time, but as of writing:
 * - Failed tasks (message and errors) will be shown unless `--quiet`.
 * - Successful tasks will only be shown with `--verbose` or `--debug`, or if the command sets
 *   `showCompleted` (`cloudpack bundle` does as of writing).
 */
export function formatBundleTaskResult(bundleRequest: BundleRequest, reporter: TaskReporter): TaskReporterTaskResult {
  const { result, packageName, version, packagePath } = bundleRequest;

  if (!result) {
    return {
      status: 'fail',
      details: `[bundleTask] No result was found for "${packageName}@${version}"`,
    };
  }

  result.outputFiles ??= [];
  result.errors ??= [];
  result.warnings ??= [];

  if (!result.outputFiles.length && !result.errors?.length) {
    result.warnings.push({
      text: `No output files were found for "${packageName}" in "${result.outputPath}"`,
      source: 'bundleTask',
    });
  }

  const errors = result.errors?.map((error) => {
    const { text, source, location } = error;
    const filePath = location ? ' ' + formatLocation({ ...location, fromPath: packagePath }) : '';
    return `[${source}]${filePath}\n${red(indent(text, 1))}`;
  });
  const hasErrors = result.errors.length > 0;
  const status = hasErrors ? 'fail' : 'complete';

  const extraMessages: string[] = [];
  if (result.bundler) {
    extraMessages.push(`bundler: ${result.bundler}`);
  }
  const source = bundleRequest.result?.bundleSource;
  // If the result is from running the bundler, don't note anything extra
  if (source && source !== 'bundler') {
    // Simplify "local-cache" to "cache", or remove the dash from "remote-cache"
    extraMessages.push(`from ${source === 'local-cache' ? 'cache' : source.replace('-', ' ')}`);
  }
  const extraMessage = extraMessages.join(', ');

  // The completion message will show up after the task name, like this:
  //   Bundle foo@1.0.0 - 1 error found (bundler: ori)
  //   Bundle foo@1.0.0 - bundler: ori, from cache
  // Note that this will only be shown if --verbose or --debug was set, or the command set the
  // showCompleted option for TaskReporter.
  const message = hasErrors ? `${plural(result.errors.length, 'error$s found')} (${extraMessage})` : extraMessage;

  // Only shown with --verbose or --debug. Don't calculate if not needed.
  const details = reporter.getOptions().showTaskDetails
    ? bulletedList([
        `Input path: ${bundleRequest.packagePath}`,
        `Entries:`,
        Object.values(result.entries || {}),
        `Output path: ${result.outputPath}`,
        `Output files:`,
        result.outputFiles.map(
          (file) =>
            `${normalizeRelativePath(file.outputPath)} (${getFileSizeSync(
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- there are output files, so this is set
              path.resolve(result.outputPath!, file.outputPath),
            )})`,
        ),
      ])
    : undefined;

  return {
    status,
    message,
    errors,
    details,
  };
}
